import { useMemo } from 'react';
import { useCanvasStore } from '../../store/canvas-store';
import type { CodeFrame } from '../../store/types';
import { framesById } from '../../store/commands';
import { resolveInstance } from '../../store/resolve-instance';
import { HtmlEditor } from './HtmlEditor';
import { formatHtml } from './formatHtml';
import { useReadOnly } from '../readonly';

interface Props { frame: CodeFrame }

export function CodeFrameView({ frame }: Props) {
  const dispatch = useCanvasStore((s) => s.dispatch);
  const frames = useCanvasStore((s) => s.doc.frames);
  const readOnly = useReadOnly();
  const anchor = useMemo(
    () => (frame.anchorId ? frames.find((f) => f.id === frame.anchorId) : undefined),
    [frame.anchorId, frames],
  );

  if (!frame.anchorId) {
    return (
      <div className="p-2 text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
        No anchor. Select a frame and right-click → Open code, or drag this onto a frame.
      </div>
    );
  }

  if (!anchor) {
    return (
      <div className="p-2 text-xs" style={{ color: 'var(--color-warning)' }}>
        Anchor missing: <span className="font-mono">{frame.anchorId}</span>
      </div>
    );
  }

  if (anchor.kind === 'instance') {
    const byId = framesById(frames);
    return (
      <div
        className="flex flex-col h-full text-xs"
        style={{ background: 'var(--color-surface)', color: 'var(--color-text)' }}
      >
        <div
          className="px-2 py-1"
          style={{
            background: 'rgba(245, 158, 11, 0.12)',
            color: 'var(--color-tool-code)',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          read-only · edit slots inline or open the Main
        </div>
        <div className="flex-1 min-h-0">
          <HtmlEditor value={resolveInstance(anchor, byId)} readOnly />
        </div>
      </div>
    );
  }

  if (anchor.kind !== 'free' && anchor.kind !== 'main') {
    return (
      <div className="p-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
        Anchored to {anchor.kind} — no html source.
      </div>
    );
  }

  const format = () =>
    dispatch({ t: 'frame.updateHtml', id: anchor.id, html: formatHtml(anchor.html) });

  return (
    <div
      className="flex flex-col h-full"
      style={{ background: 'var(--color-input-bg)', color: 'var(--color-text)' }}
    >
      <div
        className="flex items-center justify-end px-1.5 py-1"
        style={{ borderBottom: '1px solid var(--color-border)' }}
      >
        {readOnly ? (
          <span className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>view-only</span>
        ) : (
          <button
            type="button"
            onClick={format}
            onMouseDown={(e) => e.stopPropagation()}
            title="Format HTML (Shift-Alt-F)"
            className="px-1.5 py-0.5 text-[10px] rounded font-medium"
            style={{
              background: 'rgba(245, 158, 11, 0.12)',
              color: 'var(--color-tool-code)',
            }}
          >
            Format
          </button>
        )}
      </div>
      <div className="flex-1 min-h-0">
        {readOnly ? (
          <HtmlEditor value={anchor.html} readOnly />
        ) : (
          <HtmlEditor
            value={anchor.html}
            onChange={(html) =>
              dispatch({ t: 'frame.updateHtml', id: anchor.id, html })
            }
            onFormat={format}
          />
        )}
      </div>
    </div>
  );
}
